import React, { useContext, useState, useEffect } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import axios from "axios";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { IoShieldCheckmark, IoCardOutline, IoWalletOutline } from "react-icons/io5";
import { FiCreditCard, FiSend, FiLoader } from "react-icons/fi"; // Added FiLoader
import { CartContext } from "../context/CartProvider";
// import { AuthContext } from "../context/AuthProvider"; // User info is passed via props

// Styling for the CardElement
const cardElementOptions = {
  style: {
    base: {
      color: "#32325d",
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": {
        color: "#aab7c4",
      },
    },
    invalid: {
      color: "#fa755a",
      iconColor: "#fa755a",
    },
  },
  hidePostalCode: true, 
};

// Helper function to get ISO country code
const getCountryCode = (countryName) => {
  if (countryName === "United Kingdom") {
    return "GB";
  }
  // For Stripe, it's best to ensure Checkout.js sends ISO codes if other countries are supported.
  // This function is a fallback.
  return countryName?.toUpperCase().substring(0, 2) || "GB"; // Default to GB if undefined or not UK
};

const CheckoutForm = ({ items, userInfo }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("stripe"); // Default to stripe
  const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000/api/"; // Ensure consistent fallback
  const { removeCart } = useContext(CartContext); // Assuming this clears context/localStorage

  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState(null);

  // Use the payableAmount passed from userInfo as the source of truth for display and payment intent
  const finalPayableAmountFromProps = parseFloat(userInfo?.payableAmount || 0);

  useEffect(() => {
    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }
  }, [stripe, elements]);

  const handleCardChange = (event) => {
    setCardComplete(event.complete);
    setCardError(event.error ? event.error.message : null);
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    
    if (paymentMethod === "stripe") {
      if (!stripe || !elements || !elements.getElement(CardElement)) {
        toast.error("Payment system is not ready. Please wait or refresh the page.");
        return;
      }
      if (!cardComplete) {
          toast.error("Please complete your card details.");
          return;
      }
      if (cardError) {
          toast.error(`Card error: ${cardError}. Please check your details.`);
          return;
      }
    }
    
    // Validate final payable amount
    if (isNaN(finalPayableAmountFromProps) || finalPayableAmountFromProps <= 0) {
        toast.error("Invalid order total. Please return to cart and try again.");
        setLoading(false);
        return;
    }

    setLoading(true);

    try {
      if (paymentMethod === "stripe") {
        // Stripe expects amount in smallest currency unit (e.g., pence for GBP)
        const amountInPence = Math.round(finalPayableAmountFromProps * 100);

        if (amountInPence <= 0 && finalPayableAmountFromProps > 0) { // Check for very small amounts that round to 0 pence
            toast.error("Order total is too small for processing.");
            setLoading(false);
            return;
        }
        if (amountInPence === 0 && finalPayableAmountFromProps === 0 && items.length > 0) {
            // This case might mean a 100% discount making order free
            // Handle free orders separately if needed, or ensure payment intent handles 0 amount correctly
            // For now, assuming backend/Stripe handles 0 amount intents if that's a valid scenario.
            // If not, add specific logic here.
        }


        const intentResponse = await axios.post(`${baseUrl}order/create-payment-intent`, {
          amount: amountInPence, // Send amount in pence/cents
          email: userInfo.email, 
          currency: 'gbp', // Ensure this matches your store's currency
          // Optional: Add metadata like an order ID if you generate one pre-payment
          metadata: { 
            customerName: userInfo.userName || `${userInfo.firstName || ''} ${userInfo.lastName || ''}`.trim(),
            // You can add order_id here if you create one before payment intent
          }
        });
        const clientSecret = intentResponse.data.clientSecret;

        if (!clientSecret) {
            toast.error("Could not initialize payment. Please try again.");
            setLoading(false);
            return;
        }

        const cardEl = elements.getElement(CardElement);
        const paymentResult = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: cardEl,
            billing_details: {
              name: userInfo.userName || `${userInfo.firstName || ''} ${userInfo.lastName || ''}`.trim(),
              email: userInfo.email,
              phone: userInfo.phone,
              address: {
                line1: userInfo.streetAddress1,
                line2: userInfo.streetAddress2 || null,
                city: userInfo.city,
                postal_code: userInfo.pinCode,
                country: getCountryCode(userInfo.country),
              }
            },
          },
        });

        if (paymentResult.error) {
          toast.error(paymentResult.error.message || "Payment failed. Please try again.");
          setLoading(false);
          return;
        }

        if (paymentResult.paymentIntent.status === "succeeded") {
          const paymentId = paymentResult.paymentIntent.id;
          
          const userInfoForOrderCreation = {
            ...userInfo,
            // Country is already expected to be in a suitable format from Checkout.js
            // getCountryCode was already applied there or should be if not ISO already.
            // For consistency, ensure what's stored in userInfo.country is what backend expects.
            // Here, we assume userInfo.country is already the desired format for DB.
          };

          await axios.post(`${baseUrl}order/create-order`, { // Your backend endpoint
            items,
            paymentId,
            paymentStatus: "succeeded", // Add payment status
            paymentMethod: "stripe",
            userInfo: userInfoForOrderCreation, // Pass the already prepared userInfo
          });

          Swal.fire({
            title: "Payment Successful!",
            text: "Your order has been placed successfully.",
            icon: "success",
            confirmButtonColor: "#e5272e", 
          }).then(() => {
            removeCart(); // Clear cart from context & localStorage
            // localStorage.removeItem("cart"); // removeCart in context should handle this
            localStorage.removeItem("checkoutFormSimplified"); // Clear saved checkout form
            navigate("/myaccount", { state: { toggle: 2 } }); // Redirect to orders page
          });
        }
      } else if (paymentMethod === "cod") {
          const userInfoForCODCreation = {
            ...userInfo,
            // Ensure country consistency as above
          };

        await axios.post(`${baseUrl}order/cod`, { // Your backend endpoint for COD
          items,
          paymentStatus: "pending", // COD specific status
          paymentMethod: "cod",
          userInfo: userInfoForCODCreation, // Pass the already prepared userInfo
        });

        Swal.fire({
          title: "Order Placed!",
          text: "Your Cash on Delivery order has been successfully placed.",
          icon: "success",
          confirmButtonColor: "#e5272e",
        }).then(() => {
          removeCart();
          localStorage.removeItem("checkoutFormSimplified");
          navigate("/myaccount", { state: { toggle: 2 } });
        });
      }
    } catch (error) {
      console.error("Payment processing error:", error);
      const errorMessage = error.response?.data?.message || error.message || "An error occurred during payment processing.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  const gradientColor = "linear-gradient(to right, #e5272e, #f59332)";
  const primaryAccentColor = "#e5272e"; // Used for active elements

  const paymentMethodLabelStyle = (method) => ({
    display: 'flex', alignItems: 'center', padding: '0.75rem 1rem',
    border: `2px solid ${paymentMethod === method ? primaryAccentColor : '#dee2e6'}`,
    borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s ease-in-out',
    backgroundColor: paymentMethod === method ? 'rgba(229, 39, 46, 0.05)' : '#fff',
    boxShadow: paymentMethod === method ? `0 0 0 0.1rem rgba(229, 39, 46, 0.25)` : 'none',
  });
  
  const actionButtonStyle = {
    backgroundImage: gradientColor, color: "white", border: "none",
    padding: "0.8rem 1.5rem", borderRadius: "50px", fontWeight: "600",
    fontSize: "1.05rem", 
    transition: "opacity 0.2s ease, transform 0.15s ease, box-shadow 0.2s ease",
    width: "100%", marginTop: "1.5rem", boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
  // Hover and active state for button
  const actionButtonHoverActiveStyle = `
    .action-button-submit:hover {
        opacity: 0.9;
        box-shadow: 0 6px 15px rgba(0,0,0,0.15);
    }
    .action-button-submit:active {
        transform: translateY(1px);
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    }
  `;


  return (
    <>
    <style>
    {`
        .checkout-form-container-styled {
            background-color: #fff; /* Card background */
            padding: 2rem; /* More padding */
            border-radius: 12px; /* Consistent with Purchase page card */
            /* Removed box-shadow here, as it's applied on the Purchase page's card */
        }
        .payment-method-group .form-check {
            margin-bottom: 0; 
        }
        .payment-method-group .form-check-input {
            display: none; /* Hide actual radio button */
        }
        .payment-method-group .form-check-label {
            width: 100%; /* Make label take full width for clickable area */
        }
        .card-element-wrapper {
            padding: 0.75rem; 
            border: 1px solid #ced4da; 
            border-radius: 8px; 
            background-color: #fff;
            margin-top: 0.25rem; /* Space below label */
        }
        .card-element-wrapper .StripeElement { /* Target Stripe's own element if needed */
            padding: 0.25rem 0; 
        }
        .form-label-cardinfo {
            font-weight: 500;
            color: #343a40;
            font-size: 0.95rem;
            margin-bottom: 0.5rem;
            display: block; /* Ensure it takes width */
        }
        .error-text-stripe {
            color: #dc3545; /* Bootstrap danger color */
            font-size: 0.8rem;
            margin-top: 0.5rem;
        }
        ${actionButtonHoverActiveStyle} /* Inject hover/active styles */
        .spinner-border-sm { width: 1em; height: 1em; border-width: .2em; } /* Ensure spinner is visible */
    `}
    </style>
    <div className="checkout-form-container-styled">
      <div className="text-center mb-4">
        {/* Using IoShieldCheckmark from Purchase page for consistency */}
        {/* <IoShieldCheckmark style={{ color: "#28a745", fontSize: "2.5rem" }} /> */}
        <h3 className="fw-semibold mt-2" style={{ color: "#333" }}>Payment Details</h3>
        <p className="text-muted mb-1">Total Payable: 
            <strong style={{ color: primaryAccentColor, fontSize: '1.2em' }}> £{finalPayableAmountFromProps.toFixed(2)}</strong>
        </p>
      </div>

      <div className="mb-4">
        <p className="text-muted text-center mb-3 small fw-medium">Select Payment Method</p>
        <div className="d-grid gap-3 payment-method-group">
          <div className="form-check">
            <input
              type="radio" id="stripePayment" name="paymentMethod" value="stripe"
              checked={paymentMethod === "stripe"}
              onChange={() => setPaymentMethod("stripe")}
              className="form-check-input"
            />
            <label htmlFor="stripePayment" className="form-check-label" style={paymentMethodLabelStyle("stripe")}>
              <IoCardOutline size={24} style={{ marginRight: '10px', color: paymentMethod === "stripe" ? primaryAccentColor : '#6c757d' }} /> 
              Pay with Card
            </label>
          </div>
          <div className="form-check">
            <input
              type="radio" id="codPayment" name="paymentMethod" value="cod"
              checked={paymentMethod === "cod"}
              onChange={() => setPaymentMethod("cod")}
              className="form-check-input"
            />
            <label htmlFor="codPayment" className="form-check-label" style={paymentMethodLabelStyle("cod")}>
              <IoWalletOutline size={24} style={{ marginRight: '10px', color: paymentMethod === "cod" ? primaryAccentColor : '#6c757d' }}/> 
              Cash on Delivery
            </label>
          </div>
        </div>
      </div>

      <form onSubmit={handlePayment} noValidate>
        {paymentMethod === "stripe" && (
          <>
            <div className="form-group mb-3">
              <label htmlFor="card-element" className="form-label-cardinfo">
                <FiCreditCard className="me-2" /> Card Information
              </label>
              <div className="card-element-wrapper">
                <CardElement 
                    id="card-element" 
                    options={cardElementOptions}
                    onChange={handleCardChange}
                />
              </div>
              {cardError && <p className="error-text-stripe mt-2">{cardError}</p>}
            </div>
          </>
        )}

        <button
          type="submit"
          disabled={loading || (paymentMethod === "stripe" && (!stripe || !elements || !elements.getElement(CardElement) || !cardComplete || !!cardError))}
          className="btn action-button-submit" // Added class for hover/active
          style={actionButtonStyle}
        >
          {loading 
            ? (
                <>
                  <FiLoader className="spinner-border spinner-border-sm me-2" /> {/* Using FiLoader with spinner class */}
                  {paymentMethod === "stripe" ? "Processing Payment..." : "Placing Order..."}
                </>
              ) 
            : (paymentMethod === "stripe" ? <><FiSend className="me-2"/>Pay Securely £{finalPayableAmountFromProps.toFixed(2)}</> 
                                          : <><FiSend className="me-2"/>Place Order (COD)</>)}
        </button>
        {paymentMethod === "stripe" && 
            <p className="text-center text-muted mt-3 small d-flex align-items-center justify-content-center">
                <IoShieldCheckmark style={{ color: "green", marginRight: '5px' }} size={16} /> All transactions are secure and encrypted.
            </p>
        }
      </form>
    </div>
    </>
  );
};

export default CheckoutForm;
